import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ArrowLeft, Check, FileText, Search, SlidersHorizontal, Sparkles, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge, LangBadge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { EmptyState, Skeleton, Stat } from '@/components/ui/misc'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { PromptEditor } from './prompt-editor'
import { CollectionPicker } from '@/pages/term-base/_components/collection-picker'
import { getCollections } from '@/lib/api/collections/get-collections'
import { useQuery } from '@tanstack/react-query'
import { cn, formatNumber, highlightTerms } from '@/lib/utils'
import type { PreviewChunk, PreviewResult, PreviewTerm } from '@/types/preview'

interface PreviewResultsProps {
  result: PreviewResult
  chunks: PreviewChunk[]
  textLoading: boolean
  busy: boolean
  domains: string
  onCommit: (collectionId: number | null) => void
  onDiscard: () => void
  onBack: () => void
  onRerun: (r: PreviewResult) => void
  /** Pre-selected commit target when arriving from a collection. */
  defaultCollectionId?: number | null
  /** Lemmas from the run before this one, so a re-run can show what changed. */
  previousLemmas?: Set<string>
}

const termKey = (t: PreviewTerm) => `${t.lemma}|${t.lang}`

export function PreviewResults({
  result,
  chunks,
  textLoading,
  busy,
  domains,
  onCommit,
  onDiscard,
  onBack,
  onRerun,
  previousLemmas,
  defaultCollectionId,
}: PreviewResultsProps) {
  const { t } = useTranslation()
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  const [pane, setPane] = useState<'text' | 'prompt'>('text')
  // Which collection this document and its terms should join on commit.
  const collections = useQuery({ queryKey: ['collections'], queryFn: getCollections })
  const [target, setTarget] = useState<number | null>(null)
  const targetId =
    target ?? defaultCollectionId ?? collections.data?.[0]?.id ?? null
  const textRef = useRef<HTMLDivElement>(null)

  const terms = useMemo(() => {
    const q = filter.trim().toLowerCase()
    const list = q
      ? result.terms.filter(
          (t) => t.lemma.toLowerCase().includes(q) || (t.domain ?? '').toLowerCase().includes(q),
        )
      : result.terms
    return [...list].sort((a, b) => b.mentions.length - a.mentions.length)
  }, [result.terms, filter])

  /** Every surface form seen in the document, mapped back to its term. */
  const surfaceToKey = useMemo(() => {
    const map = new Map<string, string>()
    for (const t of result.terms) {
      const key = termKey(t)
      map.set(t.lemma.toLowerCase(), key)
      for (const m of t.mentions) {
        if (m.surface) map.set(m.surface.toLowerCase(), key)
      }
    }
    return map
  }, [result.terms])

  // Segmenting ~90k characters against several hundred surfaces is the most
  // expensive thing on this page, so it happens once per document, not per render.
  const segmented = useMemo(
    () => chunks.map((c) => ({ page: c.page_number, segments: highlightTerms(c.text, surfaceToKey) })),
    [chunks, surfaceToKey],
  )

  // Jump the text pane to the first occurrence of whatever the user picked.
  useLayoutEffect(() => {
    if (!selected || !textRef.current) return
    const el = textRef.current.querySelector<HTMLElement>('[data-selected="true"]')
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [selected])

  // Escape clears the selection — cheaper than hunting for the active chip.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSelected(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const selectedTerm = result.terms.find((t) => termKey(t) === selected)

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-border px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="icon-sm" onClick={onBack} title={t('common.back')}>
            <ArrowLeft />
          </Button>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold">{result.title}</h2>
            <p className="truncate text-xs text-muted-foreground">{result.filename}</p>
          </div>
          <Badge variant="warning">{t('preview.uncommitted')}</Badge>
        </div>

        {result.metadata && Object.values(result.metadata).some((v) => v) && (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-[11px]">
            <span className="flex items-center gap-1 font-medium text-muted-foreground">
              <Sparkles className="size-3 text-primary" />
              {t('preview.detected')}
            </span>
            {result.metadata.author && <span>{result.metadata.author}</span>}
            {!!result.metadata.publication_year && (
              <span className="tabular-nums">{result.metadata.publication_year}</span>
            )}
            {result.metadata.publisher && <span>{result.metadata.publisher}</span>}
            {result.metadata.document_type && result.metadata.document_type !== 'other' && (
              <Badge variant="outline">{result.metadata.document_type}</Badge>
            )}
            {result.metadata.languages && (
              <span className="text-muted-foreground">{result.metadata.languages}</span>
            )}
            <span className="ml-auto text-muted-foreground">{t('preview.detectedHint')}</span>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-end gap-6">
          <Stat label={t('preview.statTerms')} value={formatNumber(result.term_count)} />
          <Stat label={t('preview.statMentions')} value={formatNumber(result.mention_count)} />
          <Stat
            label={t('preview.statChunks')}
            value={formatNumber(chunks.length || result.chunks)}
          />
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground">{t('preview.commitInto')}</span>
            <CollectionPicker
              collections={collections.data ?? []}
              value={targetId}
              onChange={setTarget}
            />
            <Button variant="outline" onClick={onDiscard} disabled={busy}>
              <Trash2 />
              {t('preview.discard')}
            </Button>
            <Button onClick={() => onCommit(targetId)} disabled={busy || !targetId}>
              <Check />
              {t('preview.commitPermanently')}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <aside className="flex w-[340px] shrink-0 flex-col border-r border-border">
          <div className="shrink-0 border-b border-border p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder={t('preview.filterTerms')}
                className="pl-8"
              />
            </div>
          </div>
          <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
            {terms.map((term) => {
              const key = termKey(term)
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelected(selected === key ? null : key)}
                  className={cn(
                    'flex w-full items-center gap-2 border-b border-border/60 px-3 py-1.5 text-left transition-colors',
                    selected === key ? 'bg-accent' : 'hover:bg-muted',
                  )}
                >
                  <LangBadge lang={term.lang} />
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate text-[13px]',
                      selected === key && 'font-semibold',
                    )}
                  >
                    {term.lemma}
                  </span>
                  {term.domain && (
                    <span className="hidden max-w-24 truncate text-[10px] text-muted-foreground lg:block">
                      {term.domain}
                    </span>
                  )}
                  {previousLemmas && !previousLemmas.has(term.lemma.toLowerCase()) && (
                    <Badge variant="success" size="sm" title={t('preview.newTermHint')}>
                      {t('preview.newTerm')}
                    </Badge>
                  )}
                  <span className="tabular-nums text-[11px] text-muted-foreground">
                    {term.mentions.length}
                  </span>
                </button>
              )
            })}
            {!terms.length && (
              <p className="p-4 text-center text-xs text-muted-foreground">
                {t('common.noMatches')}
              </p>
            )}
          </div>
        </aside>

        <section className="flex min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-1.5 text-[11px] text-muted-foreground">
            <ToggleGroup
              type="single"
              value={pane}
              onValueChange={(v) => v && setPane(v as 'text' | 'prompt')}
            >
              <ToggleGroupItem value="text">
                <FileText />
                {t('preview.paneText')}
              </ToggleGroupItem>
              <ToggleGroupItem value="prompt">
                <SlidersHorizontal />
                {t('preview.panePrompt')}
              </ToggleGroupItem>
            </ToggleGroup>
            {pane === 'text' && selectedTerm && (
              <>
                <span>·</span>
                <span className="font-medium text-foreground">{selectedTerm.lemma}</span>
                <span>{t('units.mentions', { count: selectedTerm.mentions.length })}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  onClick={() => setSelected(null)}
                >
                  {t('common.clearSelection')}
                </Button>
              </>
            )}
          </div>

          {pane === 'prompt' ? (
            <div className="min-h-0 flex-1">
              <PromptEditor sessionId={result.session_id} domains={domains} onResult={onRerun} />
            </div>
          ) : (
          <div ref={textRef} className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {textLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} className="h-4 w-full" />
                ))}
              </div>
            ) : !segmented.length ? (
              <EmptyState title={t('documents.textLoadFailed')} />
            ) : (
              <div className="mx-auto max-w-3xl space-y-3 text-[13px] leading-relaxed">
                {segmented.map((chunk, ci) => (
                  <p key={ci}>
                    {chunk.page > 0 && (
                      <span className="mr-2 select-none rounded bg-muted px-1 text-[10px] text-muted-foreground">
                        {t('common.page', { page: chunk.page })}
                      </span>
                    )}
                    {chunk.segments.map((seg, si) =>
                      seg.termKey ? (
                        <mark
                          key={si}
                          data-selected={seg.termKey === selected ? 'true' : undefined}
                          onClick={() => setSelected(seg.termKey)}
                          className={cn(
                            'cursor-pointer rounded-sm px-0.5',
                            seg.termKey === selected
                              ? 'bg-primary font-semibold text-primary-foreground'
                              : 'bg-highlight/50 text-highlight-foreground hover:bg-highlight',
                          )}
                        >
                          {seg.text}
                        </mark>
                      ) : (
                        <span key={si}>{seg.text}</span>
                      ),
                    )}
                  </p>
                ))}
              </div>
            )}
          </div>
          )}
        </section>
      </div>
    </div>
  )
}
